import {
  DDDeveloperListing,
  DDUnitType,
  DDAgentListing,
  resolvePGImageUrl,
  DDListingsResponse,
  DDListing,
} from '../../../lib/ddListingTypes';

interface BedroomGroup {
  label: string;
  bedroomCount: number;
  units: DDUnitType[];
}

// UnitTypesFromDeveloper helpers
export const getBedroomLabel = (count: number): string =>
  count > 0 ? `${count} ห้องนอน` : 'อื่น ๆ';

export const groupByBedrooms = (unitTypes: DDUnitType[]): BedroomGroup[] => {
  const map = new Map<number, DDUnitType[]>();

  unitTypes.forEach((unit) => {
    const count = unit.configuration?.bedrooms?.value ?? 0;
    if (!map.has(count)) map.set(count, []);
    map.get(count).push(unit);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => {
      if (a === 0) return 1; // push 0 bedrooms to end
      if (b === 0) return -1;
      return a - b;
    })
    .map(([count, units]) => ({
      bedroomCount: count,
      label: getBedroomLabel(count),
      units,
    }));
};

export const getFloorPlans = (
  unit: DDUnitType,
  listing: DDDeveloperListing,
) => {
  // Check listing-level floor plans first (matched by unit.id)
  const listingPlans = listing.media?.unitTypes?.listing ?? [];
  const listingMatch = listingPlans.find((p) => p.unitTypeId === unit.id);
  if (listingMatch?.floorPlans?.length) {
    return listingMatch.floorPlans;
  }

  // Fall back to project-level floor plans (matched by unit.unitTypeId)
  const projectPlans = listing.media?.unitTypes?.project ?? [];
  const projectMatch = projectPlans.find(
    (p) => p.unitTypeId === unit.unitTypeId,
  );
  return projectMatch?.floorPlans ?? [];
};

export const getFloorSize = (unit: DDUnitType): string => {
  const floor = unit.dimensions?.floor?.[0]?.size;
  if (!floor) return '';
  const min = floor.min;
  if (!min) return '';
  return `${min.value} ตารางเมตร`;
};

// UnitsFromAgent helpers
export const getSubtitle = (listing: DDAgentListing): string => {
  const levels = listing.location?.levels ?? [];
  const district = levels.find((l) => l.levelName === 'district')?.value ?? '';
  const province = levels.find((l) => l.levelName === 'province')?.value ?? '';
  return [district, province].filter(Boolean).join(', ');
};

export const getPropertyType = (listing: DDAgentListing): string => {
  return listing.project?.metaByType?.verified?.property?.type?.text ?? '';
};

export const getBedrooms = (listing: DDAgentListing): number | null => {
  return listing.unitDetails?.configuration?.bedrooms?.value ?? null;
};

export const getBathrooms = (listing: DDAgentListing): number | null => {
  return listing.unitDetails?.configuration?.bathrooms?.value ?? null;
};

export const getCoverImage = (listing: DDAgentListing): string | null => {
  const cover = listing.media?.cover?.[0];
  if (!cover?.urlTemplate) return null;
  return resolvePGImageUrl(cover.urlTemplate);
};

export const getBedroomDisplay = (
  count: number | null,
): number | string | null => {
  if (count === null) return null;
  if (count === 0) return 'Studio';
  return count;
};

export const getDeveloperListing = (
  listings: DDListingsResponse['listings'],
): DDDeveloperListing | undefined =>
  listings.find((l) => l.lister?.type === 'developer') as
    | DDDeveloperListing
    | undefined;

export const getAgentListings = (
  listings: DDListingsResponse['listings'],
): DDAgentListing[] =>
  listings.filter(
    (l) => l.lister?.type === 'agent' && l.listingType?.code === 'SALE',
  ) as DDAgentListing[];

export const getProjectName = (
  listings: DDListingsResponse['listings'],
): string => listings[0]?.project?.metaByType?.verified?.name ?? '';

export const getListingIdentifier = (listing: DDListing): string => {
  const projectMeta = listing?.project?.metaByType?.verified;
  const developer = projectMeta?.developer ?? 'Unknown Developer';
  const projectName = projectMeta?.name ?? 'Unknown Project';
  const projectId = projectMeta?.id ?? 'Unknown ID';
  return `${developer} - ${projectName} - ${projectId}`;
};
